import { useQuery } from "@tanstack/react-query";
import { Check, Building2 } from "lucide-react";
import { useTranslate, useUserMenu } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { oidcSignIn } from "../login/authConfig";
import {
  currentMembership,
  getMe,
  rememberWorkspace,
} from "../providers/api/client";

// Switching reloads the app: every cache (including the mobile app's persisted one) starts clean
// in the new workspace. Membership is still checked by the API on every request.
const switchTo = (id: string) => {
  rememberWorkspace(id);
  window.location.assign("/");
};

function useWorkspaces() {
  const { data } = useQuery({
    queryKey: ["crm.workspaces"],
    queryFn: async () => ({
      memberships: (await getMe()).memberships,
      current: (await currentMembership())?.workspace.id,
    }),
    enabled: oidcSignIn,
  });
  return data && data.memberships.length > 1 ? data : null;
}

/** The desktop user menu's workspace section; shown only to members of several workspaces. */
export const WorkspaceMenu = () => {
  const translate = useTranslate();
  const userMenu = useUserMenu();
  const workspaces = useWorkspaces();
  if (!workspaces) return null;
  return (
    <>
      <DropdownMenuLabel>
        {translate("crm.workspaces.switch", { _: "Workspaces" })}
      </DropdownMenuLabel>
      {workspaces.memberships.map(({ workspace }) => (
        <DropdownMenuItem
          key={workspace.id}
          className="flex items-center gap-2"
          onClick={() => {
            userMenu?.onClose();
            if (workspace.id !== workspaces.current) switchTo(workspace.id);
          }}
        >
          {workspace.id === workspaces.current ? <Check /> : <Building2 />}
          {workspace.name}
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
    </>
  );
};

/** The mobile settings page's workspace section. */
export const WorkspaceSwitcherMobile = () => {
  const translate = useTranslate();
  const workspaces = useWorkspaces();
  if (!workspaces) return null;
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1">
        {translate("crm.workspaces.switch", { _: "Workspaces" })}
      </p>
      {workspaces.memberships.map(({ workspace }) => (
        <Button
          key={workspace.id}
          variant={
            workspace.id === workspaces.current ? "secondary" : "outline"
          }
          className="w-full justify-start gap-2"
          onClick={() =>
            workspace.id !== workspaces.current && switchTo(workspace.id)
          }
        >
          {workspace.id === workspaces.current ? <Check /> : <Building2 />}
          {workspace.name}
        </Button>
      ))}
    </div>
  );
};
